import React from 'react'
import PropTypes from 'prop-types'
import trustCopy, { isThaiLanguage } from './copy'
import styles from './styles.scss'

export const formatSold = (count) => {
  if (!Number.isSafeInteger(count) || count <= 0) return null
  return count >= 1000 ? `${Math.floor(count / 1000)}k+` : String(count)
}

export const DesktopTrust = ({
  rating,
  salesCount,
  onReviews,
  t,
  language,
}) => {
  const sold = formatSold(salesCount)
  const hasRating = Number.isFinite(rating) && rating > 0 && rating <= 5
  if (!sold && !hasRating) return null
  return (
    <div className="desktop-trust">
      <style jsx>{styles}</style>
      {hasRating && (
        <strong>
          <span aria-hidden="true">★ </span>
          {rating}
        </strong>
      )}
      {sold && (
        <span className="sold">
          (
          {isThaiLanguage(language)
            ? `${trustCopy(t, 'SOLD', language)} ${sold}`
            : `${sold} ${trustCopy(t, 'SOLD', language)}`}
          )
        </span>
      )}
      {hasRating && (
        <span className="reviews">
          <span className="review-separator" aria-hidden="true">
            ·
          </span>
          <button type="button" onClick={onReviews}>
            {trustCopy(t, 'REVIEWS', language)}
          </button>
        </span>
      )}
    </div>
  )
}
DesktopTrust.defaultProps = { rating: null, salesCount: null, language: 'en' }
DesktopTrust.propTypes = {
  rating: PropTypes.number,
  salesCount: PropTypes.number,
  onReviews: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
}

export const DesktopModel = ({ model, t, language }) => {
  if (!model || !(model.height > 0) || !model.size) return null
  const measures = ['bust', 'waist', 'hips'].filter((key) => model[key] > 0)
  return (
    <div className="desktop-model">
      <style jsx>{styles}</style>
      <span>
        {trustCopy(t, 'MODEL', language)}: {model.height} cm ·{' '}
        {trustCopy(t, 'WEARS', language)} {model.size}
      </span>
      {measures.length > 0 && (
        <details>
          <summary>{trustCopy(t, 'MEASUREMENTS', language)}</summary>
          <dl>
            {measures.map((key) => (
              <React.Fragment key={key}>
                <dt>{trustCopy(t, key.toUpperCase(), language)}</dt>
                <dd>{model[key]}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      )}
    </div>
  )
}
DesktopModel.defaultProps = { model: null, language: 'en' }
DesktopModel.propTypes = {
  model: PropTypes.shape({
    height: PropTypes.number,
    size: PropTypes.string,
    bust: PropTypes.number,
    waist: PropTypes.number,
    hips: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
}

export const DesktopReturns = ({ product, country, language, t }) => {
  const standardThaiReturns =
    country === 'th' && !product.is_italist && product.is_returnable === true
  let description = trustCopy(t, 'POLICY', language)
  if (product.is_returnable === false)
    description = trustCopy(t, 'NON_RETURNABLE', language)
  else if (standardThaiReturns)
    description = trustCopy(t, 'RETURN_OPTIONS', language)
  return (
    <a
      className="desktop-returns"
      href={`/${country}/${language}/info/${
        product.is_italist ? 'italist' : 'returns'
      }`}
    >
      <style jsx>{styles}</style>
      <svg
        className="return-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M9 4 4 9l5 5M4 9h9a6 6 0 0 1 0 12h-2" />
      </svg>
      <span className="return-copy">
        <strong>
          {standardThaiReturns
            ? trustCopy(t, 'TH_RETURNS', language)
            : trustCopy(t, 'RETURNS', language)}
        </strong>
        {description && <span>{description}</span>}
      </span>
      <span className="return-chevron" aria-hidden="true">
        ›
      </span>
    </a>
  )
}
DesktopReturns.propTypes = {
  product: PropTypes.shape({
    is_returnable: PropTypes.bool,
    is_italist: PropTypes.bool,
  }).isRequired,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}
